import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Music, Pen, Send, ChevronRight, Shield, Clock, Play, Pause, Volume2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [examples, setExamples] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(r => setReviews(r.data)).catch(() => {});
    axios.get(`${API}/examples`).then(r => setExamples(r.data)).catch(() => {});
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { setCurrentTime(audio.currentTime); setDuration(audio.duration || 0); }
  }, []);

  const handleEnded = useCallback(() => setPlayingId(null), []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    return () => { audio.removeEventListener("timeupdate", handleTimeUpdate); audio.removeEventListener("ended", handleEnded); };
  }, [handleTimeUpdate, handleEnded]);

  const startQuiz = () => navigate("/quiz");

  const playExample = (ex) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingId === ex.id) { audio.pause(); setPlayingId(null); }
    else { audio.src = ex.song_url; audio.play(); setPlayingId(ex.id); }
  };

  const formatTime = (t) => (!t || isNaN(t)) ? "0:00" : `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}`;
  const formatDuration = (sec) => sec ? `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, "0")}` : "";

  const OCCASION_LABELS = { "Birthday": "Birthday Song", "Wedding": "Wedding Song", "Memorial / In Memory": "Memorial Song", "Anniversary": "Anniversary Song" };
  const OCCASION_TAGLINES = { "Birthday": "A personalized birthday celebration", "Wedding": "A custom first-dance love story", "Memorial / In Memory": "A tribute to someone deeply missed", "Anniversary": "30 years of love, in one song" };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <audio ref={audioRef} preload="none" />

      {/* Top Bar */}
      <div data-testid="top-bar" className="bg-[#7D1B1B] text-white text-center py-2.5 px-4 text-sm font-body">
        <span className="opacity-90">2,500+ Songs Created</span>
        <span className="mx-3 opacity-40">|</span>
        <span className="opacity-90">100% Money-Back Guarantee</span>
      </div>

      {/* Nav */}
      <nav data-testid="navbar" className="sticky top-0 z-50 bg-[#F5F5F5]/95 backdrop-blur-md border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <Music className="h-6 w-6 text-[#7D1B1B]" strokeWidth={1.5} />
            <span className="font-display text-xl font-bold text-[#2C2C2C]">StorytoSong</span>
          </div>
          <button data-testid="nav-cta-btn" onClick={startQuiz}
            className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-6 py-2.5 font-bold text-sm transition-all transform hover:-translate-y-0.5 shadow-sm">
            Create My Custom Song
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section data-testid="hero-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-2 mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-5 w-5 fill-[#D4A853] text-[#D4A853]" />)}
              <span className="text-sm text-[#5A5A5A] ml-1">2,500+ happy customers</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C2C2C] leading-tight mb-6">
              You Know the Memories. We&apos;ll Turn Them Into Music.
            </h1>
            <p className="text-base sm:text-lg text-[#5A5A5A] mb-8 max-w-lg leading-relaxed">
              Surprise someone you love with a one-of-a-kind song made from YOUR story. Their names. Your memories. A real song they'll play on repeat.
            </p>
            <button data-testid="hero-cta-btn" onClick={startQuiz}
              className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-lg shadow-lg shadow-[#7D1B1B]/20 transition-all transform hover:-translate-y-1 hover:shadow-xl">
              Create My Custom Song
              <ChevronRight className="inline-block ml-2 h-5 w-5" />
            </button>
            <p className="mt-4 text-sm text-[#5A5A5A] flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1"><Shield className="h-4 w-4 text-[#7D1B1B]" /> 100% Money-Back Guarantee</span>
            </p>
          </div>
          <div className="relative animate-fade-in delay-200">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-black/10 aspect-[4/5] max-h-[500px]">
              <img src="https://customer-assets.emergentagent.com/job_song-stories-2/artifacts/ee7raf6t_Storytosong_hero.png"
                alt="Emotional reaction to gift" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-[#E0E0E0] max-w-[220px]">
              <div className="flex items-center gap-2 mb-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-[#D4A853] text-[#D4A853]" />)}
              </div>
              <p className="text-xs text-[#5A5A5A] italic">&ldquo;The best gift I&apos;ve ever given&rdquo;</p>
              <p className="text-xs font-bold mt-1 text-[#2C2C2C]">Margaret T.</p>
            </div>
          </div>
        </div>
        <div data-testid="social-proof-strip" className="mt-16 flex flex-wrap items-center justify-center gap-6 text-sm text-[#5A5A5A]">
          <span className="flex items-center gap-2"><Music className="h-4 w-4 text-[#7D1B1B]" /> Their name woven into every verse</span>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#7D1B1B]" /> Delivered by email in 24-48h</span>
          <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#7D1B1B]" /> Don&apos;t love it? Full refund. No questions.</span>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="how-it-works-section" className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-[#2C2C2C] mb-4">Your Song, Done in 3 Easy Steps</h2>
          <p className="text-center text-[#5A5A5A] mb-14 text-base max-w-xl mx-auto">It takes about 3 minutes to fill out. We handle the rest.</p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Pen, step: "1", title: "Tell Us Your Story", desc: "Answer a few quick questions about who the song is for, the occasion, and the memories that matter most." },
              { icon: Music, step: "2", title: "We Write & Produce", desc: "Our team turns your answers into original lyrics and a studio-quality track in your chosen genre." },
              { icon: Send, step: "3", title: "Hit Play & Watch", desc: "Your finished song arrives by email. Just press play and watch their reaction." },
            ].map((s, i) => (
              <div key={i} className="text-center p-8 rounded-2xl hover:bg-[#F0F0F0] transition-all duration-300 group">
                <div className="w-16 h-16 rounded-full bg-[#F9EDED] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#7D1B1B] transition-colors">
                  <s.icon className="h-7 w-7 text-[#7D1B1B] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <div className="text-sm font-bold text-[#7D1B1B] mb-2">Step {s.step}</div>
                <h3 className="font-display text-xl font-bold text-[#2C2C2C] mb-3">{s.title}</h3>
                <p className="text-[#5A5A5A] text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <button data-testid="how-it-works-cta" onClick={startQuiz}
              className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-base shadow-lg shadow-[#7D1B1B]/20 transition-all transform hover:-translate-y-1">
              Create My Custom Song
            </button>
          </div>
        </div>
      </section>

      {/* Song Examples */}
      {examples.length > 0 && (
        <section data-testid="song-examples-section" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-[#2C2C2C] mb-4">Hear Real Songs We&apos;ve Created</h2>
          <p className="text-center text-[#5A5A5A] mb-14 text-base max-w-xl mx-auto">Every song is unique — written from real stories, produced with studio-quality AI vocals.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {examples.map((ex) => {
              const isPlaying = playingId === ex.id;
              const label = OCCASION_LABELS[ex.occasion] || ex.occasion;
              const tagline = OCCASION_TAGLINES[ex.occasion] || `A custom ${ex.genre} song`;
              return (
                <div key={ex.id} data-testid={`example-card-${ex.id}`}
                  className="bg-white rounded-2xl border border-[#E0E0E0] shadow-sm hover:shadow-lg hover:shadow-[#7D1B1B]/5 transition-all overflow-hidden group">
                  <div className="flex items-stretch">
                    <div className="relative w-32 sm:w-40 flex-shrink-0">
                      {ex.song_image_url ? (
                        <img src={ex.song_image_url} alt={label} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#7D1B1B] to-[#4A1010] flex items-center justify-center">
                          <Music className="h-8 w-8 text-white/60" />
                        </div>
                      )}
                      <button data-testid={`example-play-${ex.id}`} onClick={() => playExample(ex)}
                        className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all transform group-hover:scale-110 ${isPlaying ? "bg-white" : "bg-[#7D1B1B]"}`}>
                          {isPlaying ? <Pause className="h-5 w-5 text-[#7D1B1B]" /> : <Play className="h-5 w-5 text-white ml-0.5" />}
                        </div>
                      </button>
                    </div>
                    <div className="flex-1 p-5 flex flex-col justify-center min-h-[140px]">
                      <span className="text-xs font-bold text-[#7D1B1B] uppercase tracking-wide mb-1">{label}</span>
                      <h3 className="font-display text-lg font-bold text-[#2C2C2C] mb-1">A Song for {ex.recipient_name}</h3>
                      <p className="text-sm text-[#5A5A5A] mb-3">{tagline}</p>
                      <div className="flex items-center gap-3 text-xs text-[#888888]">
                        <span className="flex items-center gap-1"><Music className="h-3 w-3" /> {ex.genre}</span>
                        <span className="flex items-center gap-1"><Volume2 className="h-3 w-3" /> {formatDuration(ex.song_duration)}</span>
                      </div>
                      {isPlaying && (
                        <div className="mt-3">
                          <div className="w-full bg-[#E0E0E0] rounded-full h-1">
                            <div className="bg-[#7D1B1B] h-1 rounded-full transition-all" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                          </div>
                          <div className="flex justify-between mt-0.5 text-[10px] text-[#888888]">
                            <span>{formatTime(currentTime)}</span>
                            <span>{formatTime(duration)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <p className="text-sm text-[#888888] mb-4">These are real songs made from real stories. Yours will be just as personal.</p>
            <button data-testid="examples-cta" onClick={startQuiz}
              className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-base shadow-lg shadow-[#7D1B1B]/20 transition-all transform hover:-translate-y-1">
              Create My Custom Song
            </button>
          </div>
        </section>
      )}

      {/* What Mike Told Us / Lauren's Song */}
      <section data-testid="story-demo-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="bg-[#F0F0F0] rounded-2xl p-6 sm:p-8 border border-[#E0E0E0]">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-6">What Mike told us:</h3>
              <div className="space-y-4 text-sm text-[#5A5A5A] leading-relaxed">
                <div><span className="font-bold text-[#2C2C2C]">For:</span> My wife Lauren</div>
                <div><span className="font-bold text-[#2C2C2C]">Occasion:</span> 10th anniversary</div>
                <div><span className="font-bold text-[#2C2C2C]">Her qualities:</span> Most patient person I know. Sings to our kids every night even when she&apos;s running on empty. Never asks for anything for herself.</div>
                <div><span className="font-bold text-[#2C2C2C]">Memories:</span> Got caught in the rain on our first date in Chicago, ran into a bookstore and stayed for hours. She still has the book. Our daughter&apos;s first word was &ldquo;mama&rdquo; and I secretly caught it on video.</div>
                <div><span className="font-bold text-[#2C2C2C]">Message:</span> You made me better. The kids got lucky but I got luckier.</div>
              </div>
            </div>
            <div className="bg-[#2C2C2C] rounded-2xl p-6 sm:p-8 border border-[#3A3A3A]">
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">Lauren&apos;s song:</h3>
              <p className="text-white/50 text-sm mb-6">Country &middot; Female Voice &middot; Romantic / Nostalgic</p>
              <div className="font-body text-sm text-white/75 leading-relaxed space-y-4 whitespace-pre-line">
                <p className="text-[#7D1B1B] text-xs font-bold uppercase tracking-wider">[Verse 1]</p>
                <p>Chicago rain, nowhere to go{"\n"}A crowded bookstore, talking soft and slow{"\n"}You picked a novel off the shelf that night{"\n"}Ten years later, Lauren, and you&apos;re still my favorite sight</p>
                <p className="text-[#7D1B1B] text-xs font-bold uppercase tracking-wider">[Chorus]</p>
                <p>You sing them down when the whole house is quiet{"\n"}You carry it all and you never talk about it{"\n"}So here&apos;s what I should&apos;ve said a thousand times before{"\n"}Lauren, you&apos;re the one I&apos;d choose a thousand more</p>
                <p className="text-[#7D1B1B] text-xs font-bold uppercase tracking-wider">[Verse 2]</p>
                <p>Her first word was mama, caught it on my phone{"\n"}You didn&apos;t even know the camera was on{"\n"}Just you and her, that little kitchen light{"\n"}I watched it back and knew I got this life right</p>
                <p className="text-[#7D1B1B] text-xs font-bold uppercase tracking-wider">[Bridge]</p>
                <p>The kids got lucky, that&apos;s what people say{"\n"}But Lauren, no one&apos;s luckier than me today</p>
              </div>
              <div className="mt-8">
                <button data-testid="story-demo-cta" onClick={startQuiz}
                  className="bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-base shadow-lg shadow-[#7D1B1B]/20 transition-all transform hover:-translate-y-1 w-full sm:w-auto">
                  Create My Custom Song <ChevronRight className="inline-block ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section data-testid="whats-included-section" className="bg-[#F5F5F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-[#2C2C2C] mb-14">What You Get</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Music, title: "Studio-Quality Recording", desc: "A professionally produced song you'll be proud to play for anyone. Not a robot voice. Not a jingle. A real, full-length song." },
              { icon: Pen, title: "Lyrics Written From YOUR Story", desc: "Every word is pulled from the details you share. Names, dates, inside jokes — it's all there." },
              { icon: Clock, title: "Delivered Fast", desc: "Ready in as little as 24 hours with rush delivery. Standard delivery within 24-48 hours." },
              { icon: Send, title: "Shareable Link", desc: "Play it on any device. Send it to family. No apps needed. Just click and listen." },
            ].map((f, i) => (
              <div key={i} className="flex gap-5 p-6 rounded-2xl border border-[#E0E0E0] bg-white hover:border-[#7D1B1B]/30 hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl bg-[#F9EDED] flex items-center justify-center flex-shrink-0">
                  <f.icon className="h-6 w-6 text-[#7D1B1B]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-[#2C2C2C] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section data-testid="reviews-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-[#2C2C2C] mb-4">Real People. Real Songs. Real Tears.</h2>
          <p className="text-center text-[#5A5A5A] mb-14 text-base">See what our customers are saying.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((r, i) => (
              <div key={r.id || i} data-testid={`review-card-${i}`}
                className="bg-white rounded-2xl p-6 border border-[#E0E0E0] hover:border-[#7D1B1B]/20 transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <img src={r.avatar} alt={r.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <p className="font-bold text-[#2C2C2C] text-sm">{r.name}</p>
                    <span className="text-xs text-[#7D1B1B] font-medium">{r.occasion}</span>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} className="h-4 w-4 fill-[#D4A853] text-[#D4A853]" />)}
                </div>
                <p className="text-sm text-[#5A5A5A] leading-relaxed italic">&ldquo;{r.text}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" className="bg-[#F5F5F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-md mx-auto bg-white rounded-3xl p-8 border-2 border-[#7D1B1B] shadow-xl shadow-[#7D1B1B]/10 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#7D1B1B] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider whitespace-nowrap">
              The Only Gift They&apos;ll Play on Repeat
            </div>
            <h3 className="font-display text-2xl font-bold text-[#2C2C2C] mb-2 mt-2">Your Custom Song</h3>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-5xl font-bold text-[#2C2C2C]">$69</span>
            </div>
            <p className="text-sm text-[#5A5A5A] mb-8 max-w-xs mx-auto leading-relaxed">
              You can&apos;t buy this in a store. Because it doesn&apos;t exist until you tell us your story.
            </p>
            <ul className="text-left space-y-3 mb-8">
              {[
                "Original lyrics written from your words, your memories, your names",
                "Studio-quality production in 11 genres with male or female vocals",
                "Delivered straight to your inbox",
                "Don't love it? Full refund. No form. No questions.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#5A5A5A]">
                  <span className="text-[#7D1B1B] font-bold flex-shrink-0 mt-0.5">&#10003;</span>
                  {item}
                </li>
              ))}
            </ul>
            <button data-testid="pricing-cta-btn" onClick={startQuiz}
              className="w-full bg-[#7D1B1B] text-white hover:bg-[#9B2C2C] rounded-full px-8 py-4 font-bold text-lg shadow-lg shadow-[#7D1B1B]/20 transition-all transform hover:-translate-y-1">
              Create My Custom Song
            </button>
            <div className="mt-6 p-4 rounded-xl border border-[#E0E0E0] bg-[#F0F0F0] text-left">
              <p className="text-sm text-[#5A5A5A]">
                <span className="font-bold text-[#2C2C2C]">&#9889; Need it by tomorrow?</span>{" "}
                Add 24-hour rush delivery at checkout for just <span className="font-bold text-[#7D1B1B]">$30</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-20 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-center text-[#2C2C2C] mb-14">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "What makes this song special?", a: "Every song is written from scratch using YOUR words and YOUR memories. Not a template. Not AI auto-generated. A personal keepsake in music, crafted from the details you share." },
            { q: "How long until I receive the song?", a: "Standard delivery: 24-48 hours. Rush Delivery (add $30 at checkout): within 24 hours." },
            { q: "What occasions work best?", a: "Anniversaries, milestone birthdays, weddings, memorials, and \"just because\" moments. Any time you want to turn a feeling into something permanent." },
            { q: "How do you create the song?", a: "A songwriter reads your answers and writes original lyrics. Our production team brings them to life in your chosen genre with professional vocals. If you're not happy, full refund." },
            { q: "How do I get my song?", a: "We email you a private link. Click on any device. Share however you like. No apps needed." },
            { q: "Can I play it at a party?", a: "Yes! Dinners, parties, reunions, memorial services, wedding receptions. It's YOUR song to play however you want." },
            { q: "What if I don't like it?", a: "100% money-back guarantee. If it doesn't hit, every penny back. No questions asked." },
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border border-[#E0E0E0] px-6 overflow-hidden">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-[#2C2C2C] font-bold text-base py-5 hover:no-underline">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5A5A5A] text-sm leading-relaxed pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta-section" className="bg-[#7D1B1B] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold mb-6">Some Gifts Get Opened and Forgotten. This One Gets Played Over and Over.</h2>
          <p className="text-white/80 text-base mb-10 max-w-xl mx-auto leading-relaxed">
            You already know the memories. You already know the person. Tell us, and we&apos;ll turn it into a song they&apos;ll never forget.
          </p>
          <button data-testid="final-cta-btn" onClick={startQuiz}
            className="bg-white text-[#7D1B1B] hover:bg-[#F5F0EB] rounded-full px-8 py-4 font-bold text-lg shadow-lg transition-all transform hover:-translate-y-1 hover:shadow-xl">
            Create My Custom Song
            <ChevronRight className="inline-block ml-2 h-5 w-5" />
          </button>
          <p className="mt-4 text-sm text-white/60">100% Money-Back Guarantee. Takes about 3 minutes to fill out.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C2C2C] border-t border-[#3A3A3A] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-[#7D1B1B]" strokeWidth={1.5} />
            <span className="font-display text-lg font-bold text-white">StorytoSong</span>
          </div>
          <p className="text-sm text-white/50">&copy; {new Date().getFullYear()} StorytoSong. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
